import { useDynamicConfig } from "@statsig/react-bindings";
import { useEffect, useRef, useState } from "react";

import { message } from "../../components";

import { useSaveUserMemoryMutation, useUserMemoryQuery } from "./webDashboard";

// Fallback when the Statsig dynamic config is unavailable; must match
// DEFAULT_USER_MEMORY_MAX_CHARS in coeff/server/lib/agents/web_dashboard_user_memory.py.
const DEFAULT_MAX_CHARS = 2000;

// The FE reads the same config the server enforces at save time, so the counter
// and the server rule can't drift.
const USER_MEMORY_CONFIG_NAME = "web_dashboard_user_memory_config";

export interface UseMemory {
  /** The persisted note (empty string when unset). */
  savedMemory: string;
  isLoading: boolean;
  isSaving: boolean;
  /** Server-enforced char cap (Statsig-tunable). */
  maxChars: number;
  /** Editable draft + setter (the textarea binds to these). */
  draft: string;
  setDraft: (value: string) => void;
  isDirty: boolean;
  isOverLimit: boolean;
  /** view/edit toggle; true also when the note is empty (nothing to view). */
  isEditing: boolean;
  showEditor: boolean;
  startEditing: () => void;
  cancelEditing: () => void;
  /** Persist the draft. Toasts and exits edit mode on success. Saving an empty
   * draft is how the note gets cleared (there is no separate Clear action). */
  save: () => Promise<void>;
  /**
   * Persist an arbitrary value (toast only, no draft/edit-mode bookkeeping). For
   * surfaces that manage their own editor state (e.g. the rich-text drawer)
   * instead of binding to `draft`.
   */
  saveText: (text: string) => Promise<void>;
}

/**
 * Shared state machine for the memory panels — the in-viewer Tailwind modal
 * (UserMemoryDrawer) and the workspace antd modal (MemoryModal). Owns the query,
 * the save mutation, the Statsig char cap, the editable draft, and the
 * dirty-guard sync so those never drift between the two surfaces.
 *
 * @param active whether the panel is open (drives query enablement + reset).
 */
export function useMemory(active: boolean): UseMemory {
  const memoryQuery = useUserMemoryQuery(active);
  const saveMutation = useSaveUserMemoryMutation();

  const config = useDynamicConfig(USER_MEMORY_CONFIG_NAME);
  const rawMaxChars = Number(config.value?.max_chars);
  const maxChars =
    Number.isFinite(rawMaxChars) && rawMaxChars > 0 ? rawMaxChars : DEFAULT_MAX_CHARS;

  const savedMemory = memoryQuery.data ?? "";
  const [draft, setDraft] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Sync the draft to the fetched value when the panel opens, and on background
  // refetches ONLY while the user hasn't diverged from the last value we synced —
  // a refetch (window refocus, invalidation from a chat-side save) must never
  // clobber in-progress typing. lastSyncedRef
  // tracks what the textarea was last set to from the server; null = "closed,
  // resync on next open".
  const lastSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active) {
      lastSyncedRef.current = null;
      setIsEditing(false);
      return;
    }
    if (lastSyncedRef.current === null || draft === lastSyncedRef.current) {
      setDraft(savedMemory);
      lastSyncedRef.current = savedMemory;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, memoryQuery.dataUpdatedAt]);

  const isDirty = draft !== savedMemory;
  const isOverLimit = draft.length > maxChars;
  // Empty memory drops straight into the editor — there is nothing to view.
  const showEditor = isEditing || !savedMemory.trim();

  const saveText = async (text: string) => {
    try {
      await saveMutation.mutateAsync(text);
      message.success("Memory saved");
    } catch {
      message.error("Failed to save memory — please try again");
    }
  };

  const save = async () => {
    try {
      await saveMutation.mutateAsync(draft);
      // The saved draft is now the server value; future refetches may sync over it.
      lastSyncedRef.current = draft;
      setIsEditing(false);
      message.success("Memory saved");
    } catch {
      message.error("Failed to save memory — please try again");
    }
  };

  const startEditing = () => setIsEditing(true);
  const cancelEditing = () => {
    setDraft(savedMemory);
    lastSyncedRef.current = savedMemory;
    setIsEditing(false);
  };

  return {
    savedMemory,
    isLoading: memoryQuery.isInitialLoading,
    isSaving: saveMutation.isLoading,
    maxChars,
    draft,
    setDraft,
    isDirty,
    isOverLimit,
    isEditing,
    showEditor,
    startEditing,
    cancelEditing,
    save,
    saveText,
  };
}
